import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	Button,
	FormGroup,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
} from "reactstrap";
import { toast } from "react-toastify";
import { Globe, Search, X } from "lucide-react";
import {
	MONITORING_CADENCE_OPTIONS,
	MONITORING_ENGINE_OPTIONS,
	MONITORING_RUN_TIME_OPTIONS,
	MONITORING_TIMEZONE_OPTIONS,
	adaptSettingsForm,
	crawlScopeChanged,
	engineLabel,
	formatScheduleUpcoming,
	normalizeIntervalDays,
	toSettingsOverridesPayload,
	validateSettingsForm,
} from "./monitoringAdapters";
import { ProviderIcon } from "../shared/ProviderIcon";
import {
	approveMonitoringAlias,
	dismissMonitoringAlias,
	getMonitoringCrawlPages,
	getMonitoringProject,
	reauditMonitoringNow,
	updateMonitoringSettings,
} from "../../../services/monitoringApi";

const PAGE_SELECTION_OPTIONS = [
	{
		value: "auto",
		label: "Auto — grader discovery (recommended)",
	},
	{
		value: "manual",
		label: "Manual — pick specific pages",
	},
	{
		value: "all",
		label: "All site pages",
	},
];

const SORT_OPTIONS = [
	{ value: "az", label: "A–Z" },
	{ value: "za", label: "Z–A" },
	{ value: "depth", label: "Path depth" },
];

function ensureTimezoneOption(timezone) {
	if (!timezone) return MONITORING_TIMEZONE_OPTIONS;
	const exists = MONITORING_TIMEZONE_OPTIONS.some((o) => o.value === timezone);
	if (exists) return MONITORING_TIMEZONE_OPTIONS;
	return [
		...MONITORING_TIMEZONE_OPTIONS,
		{ value: timezone, label: timezone },
	];
}

function ensureRunTimeOption(runTime) {
	if (!runTime) return MONITORING_RUN_TIME_OPTIONS;
	const exists = MONITORING_RUN_TIME_OPTIONS.some((o) => o.value === runTime);
	if (exists) return MONITORING_RUN_TIME_OPTIONS;
	return [...MONITORING_RUN_TIME_OPTIONS, { value: runTime, label: runTime }];
}

function snapshotCrawlScope(form) {
	if (!form) return null;
	return {
		crawlPageMode: form.crawlPageMode || "auto",
		crawlManualUrls: Array.isArray(form.crawlManualUrls)
			? [...form.crawlManualUrls]
			: [],
	};
}

function normalizeCrawlMode(mode) {
	const m = String(mode || "auto").toLowerCase();
	if (m === "manual" || m === "all") return m;
	return "auto";
}

function sameUrlList(a, b) {
	const aa = (a || []).map(String).sort();
	const bb = (b || []).map(String).sort();
	if (aa.length !== bb.length) return false;
	return aa.every((u, i) => u === bb[i]);
}

function pathDepth(url) {
	try {
		const path = new URL(url).pathname || "/";
		return path.split("/").filter(Boolean).length;
	} catch {
		return String(url).split("/").filter(Boolean).length;
	}
}

function highlightUrlMatch(url, query) {
	const q = String(query || "").trim();
	if (!q) return url;
	const lower = String(url).toLowerCase();
	const idx = lower.indexOf(q.toLowerCase());
	if (idx < 0) return url;
	const end = idx + q.length;
	return (
		<>
			{url.slice(0, idx)}
			<mark className="monitoring__crawl-pages-mark">{url.slice(idx, end)}</mark>
			{url.slice(end)}
		</>
	);
}

function settingsFormSnapshot(form) {
	if (!form) return null;
	return JSON.stringify({
		...toSettingsOverridesPayload(form),
		brandName: String(form.brandName || "").trim(),
	});
}

function brandNameSourceNote(source) {
	if (source === "manual") {
		return "Manually set — automatic detection will not change this.";
	}
	return "Detected from your site — editing locks this name.";
}

function CadenceField({
	id,
	label,
	hint,
	cadence,
	intervalDays,
	onCadenceChange,
	onIntervalDaysChange,
	onIntervalDaysBlur,
}) {
	const showInterval = cadence === "interval";

	return (
		<FormGroup className="monitoring__settings-field monitoring__settings-field--cadence">
			<Label for={id}>{label}</Label>
			<div className="monitoring__cadence-row">
				<Input
					id={id}
					type="select"
					className="monitoring__cadence-select"
					value={cadence}
					onChange={(e) => onCadenceChange(e.target.value)}
				>
					{MONITORING_CADENCE_OPTIONS.map((opt) => (
						<option key={opt.value} value={opt.value}>
							{opt.label}
						</option>
					))}
				</Input>
				{showInterval ? (
					<>
						<Input
							id={`${id}-interval-days`}
							type="number"
							className="monitoring__cadence-interval"
							min={1}
							max={90}
							step={1}
							inputMode="numeric"
							value={intervalDays ?? ""}
							onChange={(e) => onIntervalDaysChange(e.target.value)}
							onBlur={() => onIntervalDaysBlur()}
							aria-label={`${label} interval in days`}
						/>
						<span className="monitoring__cadence-suffix">days</span>
					</>
				) : null}
			</div>
			<small className="monitoring__settings-hint text-muted">{hint}</small>
		</FormGroup>
	);
}

export default function MonitoringSettings({ websiteId }) {
	const [form, setForm] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [saving, setSaving] = useState(false);
	const [reauditOpen, setReauditOpen] = useState(false);
	const [reauditBusy, setReauditBusy] = useState(false);
	const savedCrawlRef = useRef(null);
	const savedSnapshotRef = useRef(null);
	const dirtyRef = useRef(false);
	const [isDirty, setIsDirty] = useState(false);

	const [crawlLoading, setCrawlLoading] = useState(false);
	const [crawlError, setCrawlError] = useState(null);
	const [crawl, setCrawl] = useState(null);
	const [aliasBusyName, setAliasBusyName] = useState(null);

	const [selectOpen, setSelectOpen] = useState(false);
	const [filterInput, setFilterInput] = useState("");
	const [filter, setFilter] = useState("");
	const [sortBy, setSortBy] = useState("az");
	const [draft, setDraft] = useState(() => new Set());
	const selectAllRef = useRef(null);
	const crawlSelectedRef = useRef([]);

	const timezoneOptions = useMemo(
		() => ensureTimezoneOption(form?.timezone),
		[form?.timezone]
	);

	const promptRunTimeOptions = useMemo(
		() => ensureRunTimeOption(form?.promptRunTime),
		[form?.promptRunTime]
	);

	const auditRunTimeOptions = useMemo(
		() => ensureRunTimeOption(form?.auditRunTime),
		[form?.auditRunTime]
	);

	const nextPromptRunLabel = useMemo(
		() => formatScheduleUpcoming(form?.nextPromptRunAt),
		[form?.nextPromptRunAt]
	);

	const nextReauditLabel = useMemo(
		() => formatScheduleUpcoming(form?.nextReauditAt),
		[form?.nextReauditAt]
	);

	const crawlMode = normalizeCrawlMode(form?.crawlPageMode);
	const selectedUrls = Array.isArray(form?.crawlManualUrls)
		? form.crawlManualUrls
		: [];
	crawlSelectedRef.current = selectedUrls;

	function markFormSaved(nextForm) {
		savedSnapshotRef.current = settingsFormSnapshot(nextForm);
		savedCrawlRef.current = snapshotCrawlScope(nextForm);
		dirtyRef.current = false;
		setIsDirty(false);
	}

	function syncDirtyFromForm(nextForm) {
		const dirty =
			settingsFormSnapshot(nextForm) !== savedSnapshotRef.current;
		dirtyRef.current = dirty;
		setIsDirty(dirty);
	}

	const applyProgrammaticForm = useCallback((updater) => {
		setForm((prev) => {
			if (!prev) return prev;
			const next = updater(prev);
			if (!next || next === prev) return prev;
			if (!dirtyRef.current) {
				savedSnapshotRef.current = settingsFormSnapshot(next);
				savedCrawlRef.current = snapshotCrawlScope(next);
			} else {
				syncDirtyFromForm(next);
			}
			return next;
		});
	}, []);

	const load = useCallback(async () => {
		if (!websiteId) {
			setLoading(false);
			setError("Missing website");
			setForm(null);
			return;
		}
		setLoading(true);
		setError(null);
		const res = await getMonitoringProject(websiteId);
		if (!res.ok || !res.project) {
			setError(res.message || "Failed to load settings");
			setForm(null);
			setLoading(false);
			return;
		}
		const nextForm = adaptSettingsForm(res.project);
		setForm(nextForm);
		savedSnapshotRef.current = settingsFormSnapshot(nextForm);
		savedCrawlRef.current = snapshotCrawlScope(nextForm);
		dirtyRef.current = false;
		setIsDirty(false);
		setLoading(false);
	}, [websiteId]);

	useEffect(() => {
		load();
	}, [load]);

	const applyCrawlMeta = useCallback((nextCrawl) => {
		if (!nextCrawl) return;
		applyProgrammaticForm((prev) => {
			const next = { ...prev };
			if (nextCrawl.maxPages != null) next.crawlMaxPages = nextCrawl.maxPages;
			if (!prev.crawlPageMode && nextCrawl.mode) {
				next.crawlPageMode = nextCrawl.mode;
			}
			return next;
		});
	}, [applyProgrammaticForm]);

	const loadCrawl = useCallback(async () => {
		if (!websiteId) return;
		setCrawlLoading(true);
		setCrawlError(null);
		const res = await getMonitoringCrawlPages(websiteId);
		if (!res.ok || !res.crawl) {
			setCrawlError(res.message || "Failed to load crawl pages");
			setCrawl(null);
			setCrawlLoading(false);
			return;
		}
		setCrawl(res.crawl);
		applyCrawlMeta(res.crawl);
		const currentSelected = crawlSelectedRef.current;
		if (
			(!currentSelected || currentSelected.length === 0) &&
			res.crawl.selected?.length > 0
		) {
			applyProgrammaticForm((prev) => ({
				...prev,
				crawlManualUrls: res.crawl.selected,
			}));
		}
		setCrawlLoading(false);
	}, [websiteId, applyCrawlMeta, applyProgrammaticForm]);

	useEffect(() => {
		loadCrawl();
	}, [websiteId]); // eslint-disable-line react-hooks/exhaustive-deps -- load once per website

	useEffect(() => {
		if (!selectOpen) return undefined;
		const t = setTimeout(() => setFilter(filterInput), 200);
		return () => clearTimeout(t);
	}, [filterInput, selectOpen]);

	useEffect(() => {
		if (!selectOpen) return;
		setFilterInput("");
		setFilter("");
		setSortBy("az");
		setDraft(new Set((crawlSelectedRef.current || []).map(String)));
	}, [selectOpen]);

	const filteredUrls = useMemo(() => {
		const urls = crawl?.urls || [];
		const q = filter.trim().toLowerCase();
		let list = q
			? urls.filter((u) => String(u).toLowerCase().includes(q))
			: [...urls];
		if (sortBy === "za") {
			list.sort((a, b) => String(b).localeCompare(String(a)));
		} else if (sortBy === "depth") {
			list.sort((a, b) => {
				const d = pathDepth(a) - pathDepth(b);
				if (d !== 0) return d;
				return String(a).localeCompare(String(b));
			});
		} else {
			list.sort((a, b) => String(a).localeCompare(String(b)));
		}
		return list;
	}, [crawl?.urls, filter, sortBy]);

	const selectedCount = draft.size;
	const listTotal = crawl?.total > 0 ? crawl.total : crawl?.urls?.length || 0;
	const filteredSelectedCount = useMemo(
		() => filteredUrls.reduce((n, u) => (draft.has(u) ? n + 1 : n), 0),
		[filteredUrls, draft]
	);
	const allFilteredSelected =
		filteredUrls.length > 0 && filteredSelectedCount === filteredUrls.length;
	const someFilteredSelected =
		filteredSelectedCount > 0 && !allFilteredSelected;

	useEffect(() => {
		if (!selectAllRef.current) return;
		selectAllRef.current.indeterminate = someFilteredSelected;
	}, [someFilteredSelected, selectOpen]);

	const cappedCount = useMemo(() => {
		if (!crawl) return 0;
		const maxPages = Number(crawl.maxPages) || 30;
		const ceiling = Number(crawl.ceiling) || 200;
		return Math.min(maxPages, ceiling);
	}, [crawl]);

	const crawlDescription = useMemo(() => {
		if (crawlLoading) {
			return "…Reading the site's sitemap…";
		}
		if (!crawl) {
			return "Which pages the AI Grader crawls and audits for this website on a re-audit.";
		}
		const total = crawl.total ?? crawl.urls?.length ?? 0;
		if (crawl.sitemapExists && crawl.sitemapUrl) {
			return (
				<>
					{total} total links available from{" "}
					<a
						href={crawl.sitemapUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="monitoring__crawl-sitemap-link"
						title={crawl.sitemapUrl}
					>
						View sitemap
					</a>
					.
				</>
			);
		}
		if (crawl.sitemapUrl) {
			return (
				<>
					{total} total links available from{" "}
					<a
						href={crawl.sitemapUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="monitoring__crawl-sitemap-link"
						title={crawl.sitemapUrl}
					>
						View sitemap
					</a>
					{crawl.source ? ` (source: ${crawl.source})` : ""}.
				</>
			);
		}
		return `${total} total links available${
			crawl.source ? ` (source: ${crawl.source})` : ""
		}.`;
	}, [crawlLoading, crawl]);

	function patch(partial) {
		setForm((prev) => {
			if (!prev) return prev;
			const next = { ...prev, ...partial };
			syncDirtyFromForm(next);
			return next;
		});
	}

	function toggleEngine(engine) {
		setForm((prev) => {
			if (!prev) return prev;
			const set = new Set(prev.engines || []);
			if (set.has(engine)) set.delete(engine);
			else set.add(engine);
			const next = { ...prev, engines: Array.from(set) };
			syncDirtyFromForm(next);
			return next;
		});
	}

	async function handleCrawlModeChange(nextMode) {
		const normalized = normalizeCrawlMode(nextMode);
		patch({ crawlPageMode: normalized });
		if (!websiteId) return;
		setCrawlLoading(true);
		setCrawlError(null);
		const res = await getMonitoringCrawlPages(websiteId);
		if (!res.ok || !res.crawl) {
			setCrawlError(res.message || "Failed to load crawl pages");
			setCrawlLoading(false);
			return;
		}
		setCrawl(res.crawl);
		applyCrawlMeta(res.crawl);
		setCrawlLoading(false);
	}

	function toggleDraftUrl(url) {
		setDraft((prev) => {
			const next = new Set(prev);
			if (next.has(url)) next.delete(url);
			else next.add(url);
			return next;
		});
	}

	function selectAllFiltered() {
		setDraft((prev) => {
			const next = new Set(prev);
			filteredUrls.forEach((u) => next.add(u));
			return next;
		});
	}

	function clearDraftSelection() {
		setDraft(new Set());
	}

	function handleSelectAllChange(e) {
		if (e.target.checked) selectAllFiltered();
		else {
			setDraft((prev) => {
				const next = new Set(prev);
				filteredUrls.forEach((u) => next.delete(u));
				return next;
			});
		}
	}

	function handleConfirmPages() {
		if (selectedCount < 1) return;
		const next = Array.from(draft);
		if (!sameUrlList(selectedUrls, next)) {
			patch({ crawlManualUrls: next });
		}
		setSelectOpen(false);
	}

	function closeSelectModal() {
		setSelectOpen(false);
	}

	async function handleSave(e) {
		e.preventDefault();
		if (!form || !websiteId || saving || !isDirty) return;
		const validationError = validateSettingsForm(form);
		if (validationError) {
			toast.error(validationError);
			return;
		}
		const crawlChanged = crawlScopeChanged(savedCrawlRef.current, form);
		setSaving(true);
		const payload = toSettingsOverridesPayload(form);
		const res = await updateMonitoringSettings(websiteId, payload, {
			brandName: form.brandName,
		});
		if (!res.ok) {
			setSaving(false);
			toast.error(res.message || "Could not save settings");
			return;
		}
		toast.success(res.message || "Settings saved");
		if (res.project) {
			const nextForm = adaptSettingsForm(res.project);
			setForm(nextForm);
			markFormSaved(nextForm);
		} else {
			await load();
		}
		setSaving(false);
		if (crawlChanged) {
			setReauditOpen(true);
		}
	}

	async function handleReauditNow() {
		if (!websiteId || reauditBusy) return;
		setReauditBusy(true);
		try {
			const res = await reauditMonitoringNow(websiteId);
			if (!res.ok) {
				toast.error(res.message || "Failed to start re-audit");
				setReauditBusy(false);
				return;
			}
			toast.success(
				"Re-audit started - the new report appears on the History tab when it finishes."
			);
			setReauditOpen(false);
			await load();
		} catch {
			toast.error("Failed to start re-audit");
		} finally {
			setReauditBusy(false);
		}
	}

	function handleReauditLater() {
		if (reauditBusy) return;
		setReauditOpen(false);
	}

	async function handleAliasAction(action, aliasName) {
		const name = String(aliasName || "").trim();
		if (!websiteId || !name || aliasBusyName) return;
		setAliasBusyName(name);
		try {
			const res =
				action === "approve"
					? await approveMonitoringAlias(websiteId, name)
					: await dismissMonitoringAlias(websiteId, name);
			if (!res.ok) {
				toast.error(
					res.message ||
						(action === "approve"
							? "Could not approve alias"
							: "Could not dismiss alias")
				);
				return;
			}
			toast.success(
				res.message ||
					(action === "approve" ? "Alias approved" : "Alias dismissed")
			);
			await load();
		} catch {
			toast.error(
				action === "approve"
					? "Could not approve alias"
					: "Could not dismiss alias"
			);
		} finally {
			setAliasBusyName(null);
		}
	}

	useEffect(() => {
		if (!reauditOpen) return undefined;
		const onKey = (e) => {
			if (e.key === "Escape") handleReauditLater();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [reauditOpen, reauditBusy]); // eslint-disable-line react-hooks/exhaustive-deps

	const pageCountLabel = `${selectedUrls.length} page${
		selectedUrls.length === 1 ? "" : "s"
	} selected`;

	if (loading) {
		return <p className="monitoring__empty text-muted mb-0">Loading settings…</p>;
	}
	if (error) {
		return <p className="monitoring__empty monitoring__empty--error mb-0">{error}</p>;
	}
	if (!form) {
		return (
			<p className="monitoring__empty text-muted mb-0">No settings available.</p>
		);
	}

	return (
		<form className="monitoring__settings" onSubmit={handleSave}>
			<p className="monitoring__settings-intro text-muted">
				Per-website overrides. Fields left at their default value follow the
				platform default; changing a value pins it for this website. Values are
				validated against global caps on save.
			</p>

			<section className="monitoring__panel monitoring__settings-panel">
				<h3 className="monitoring__panel-title">Brand</h3>

				<FormGroup className="monitoring__settings-field">
					<Label for="monitoring-brand-name">Brand name</Label>
					<small className="monitoring__settings-hint text-muted d-block mb-2 mt-0">
						Used to detect when AI answers mention your brand — drives mention
						rate and Share of Voice. Initially detected from your site metadata
						(schema.org / page titles); editing locks the value.
					</small>
					<Input
						id="monitoring-brand-name"
						type="text"
						value={form.brandName || ""}
						onChange={(e) =>
							patch({
								brandName: e.target.value,
								brandNameSource: "manual",
							})
						}
						placeholder="Your brand name"
					/>
					<small className="monitoring__settings-hint text-muted">
						{brandNameSourceNote(form.brandNameSource)}
					</small>
				</FormGroup>

				<FormGroup className="monitoring__settings-field">
					<Label for="monitoring-brand-aliases">Brand aliases</Label>
					<small className="monitoring__settings-hint text-muted d-block mb-2 mt-0">
						Sub-brands, DBA names, or product lines that should count as your
						brand (up to 10, comma-separated). Mentions of these count toward
						your Share of Voice and won&apos;t be treated as competitors.
					</small>
					<Input
						id="monitoring-brand-aliases"
						type="text"
						value={form.brandAliasesText}
						onChange={(e) => patch({ brandAliasesText: e.target.value })}
						placeholder="Acme, Acme Inc, Acme Corp"
					/>
				</FormGroup>

				{(form.aliasSuggestions || []).length > 0 ? (
					<div className="monitoring__alias-suggestions">
						<p className="monitoring__alias-suggestions-title">
							Suggested aliases ({form.aliasSuggestions.length}) — awaiting
							your approval
						</p>
						<p className="monitoring__alias-suggestions-lead">
							Detected as possibly the same company as yours. Approving one
							makes it count as your brand in Share of Voice and removes it
							from competitor analysis, so only approve names you own.
						</p>
						<ul className="monitoring__alias-suggestions-list">
							{form.aliasSuggestions.map((suggestion) => {
								const busy = aliasBusyName === suggestion.name;
								return (
									<li
										key={suggestion.name}
										className="monitoring__alias-suggestions-row"
									>
										<div className="monitoring__alias-suggestions-meta">
											<span className="monitoring__alias-suggestions-name">
												{suggestion.name}
											</span>
											{suggestion.evidence ? (
												<span className="monitoring__alias-suggestions-evidence">
													{suggestion.evidence}
												</span>
											) : null}
										</div>
										<div className="monitoring__alias-suggestions-actions">
											<Button
												color="primary"
												size="sm"
												type="button"
												disabled={Boolean(aliasBusyName)}
												onClick={() =>
													handleAliasAction("approve", suggestion.name)
												}
											>
												{busy ? "…" : "Approve"}
											</Button>
											<Button
												color="secondary"
												outline
												size="sm"
												type="button"
												disabled={Boolean(aliasBusyName)}
												onClick={() =>
													handleAliasAction("dismiss", suggestion.name)
												}
											>
												Dismiss
											</Button>
										</div>
									</li>
								);
							})}
						</ul>
					</div>
				) : null}
			</section>

			<section className="monitoring__panel monitoring__settings-panel">
				<h3 className="monitoring__panel-title">Schedule &amp; engines</h3>

				<div className="monitoring__settings-grid">
					<CadenceField
						id="monitoring-prompt-cadence"
						label="Prompt cadence"
						hint="How often prompts are re-run for citation / share of voice."
						cadence={form.promptCadence}
						intervalDays={form.promptIntervalDays}
						onCadenceChange={(value) => patch({ promptCadence: value })}
						onIntervalDaysChange={(value) => {
							const raw = String(value).replace(/\D/g, "");
							if (!raw) {
								patch({ promptIntervalDays: "" });
								return;
							}
							patch({
								promptIntervalDays: Math.min(90, Number(raw)),
							});
						}}
						onIntervalDaysBlur={() =>
							patch({
								promptIntervalDays: normalizeIntervalDays(
									form.promptIntervalDays,
									3
								),
							})
						}
					/>

					<FormGroup className="monitoring__settings-field">
						<Label for="monitoring-prompt-run-time">Prompt run time</Label>
						<Input
							id="monitoring-prompt-run-time"
							type="select"
							value={form.promptRunTime || "03:00"}
							onChange={(e) => patch({ promptRunTime: e.target.value })}
						>
							{promptRunTimeOptions.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</Input>
						<small className="monitoring__settings-hint text-muted">
							Local time (in the timezone below) that the prompt run starts.
						</small>
					</FormGroup>

					<CadenceField
						id="monitoring-audit-cadence"
						label="Re-audit cadence"
						hint="How often a full site re-audit runs."
						cadence={form.auditCadence}
						intervalDays={form.auditIntervalDays}
						onCadenceChange={(value) => patch({ auditCadence: value })}
						onIntervalDaysChange={(value) => {
							const raw = String(value).replace(/\D/g, "");
							if (!raw) {
								patch({ auditIntervalDays: "" });
								return;
							}
							patch({
								auditIntervalDays: Math.min(90, Number(raw)),
							});
						}}
						onIntervalDaysBlur={() =>
							patch({
								auditIntervalDays: normalizeIntervalDays(
									form.auditIntervalDays,
									3
								),
							})
						}
					/>

					<FormGroup className="monitoring__settings-field">
						<Label for="monitoring-audit-run-time">Re-audit run time</Label>
						<Input
							id="monitoring-audit-run-time"
							type="select"
							value={form.auditRunTime || "04:00"}
							onChange={(e) => patch({ auditRunTime: e.target.value })}
						>
							{auditRunTimeOptions.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</Input>
						<small className="monitoring__settings-hint text-muted">
							Local time that the re-audit starts.
						</small>
					</FormGroup>
				</div>

				{(nextPromptRunLabel || nextReauditLabel) && (
					<div
						className="monitoring__schedule-upcoming"
						role="status"
						aria-live="polite"
					>
						<div className="monitoring__schedule-upcoming-rows">
							{nextPromptRunLabel ? (
								<div className="monitoring__schedule-upcoming-row">
									<span className="monitoring__schedule-upcoming-label">
										Next prompt run
									</span>
									<span className="monitoring__schedule-upcoming-value">
										{nextPromptRunLabel}
									</span>
								</div>
							) : null}
							{nextReauditLabel ? (
								<div className="monitoring__schedule-upcoming-row">
									<span className="monitoring__schedule-upcoming-label">
										Next re-audit
									</span>
									<span className="monitoring__schedule-upcoming-value">
										{nextReauditLabel}
									</span>
								</div>
							) : null}
						</div>
						<small className="monitoring__schedule-upcoming-note text-muted">
							Shown in UTC; runs start within 5 minutes of the scheduled time.
						</small>
					</div>
				)}

				<FormGroup className="monitoring__settings-field">
					<span className="monitoring__settings-label">Engines</span>
					<div
						className="monitoring__engine-chips monitoring__engine-chips--selectable"
						role="group"
						aria-label="AI engines"
					>
						{MONITORING_ENGINE_OPTIONS.map((engine) => {
							const selected = (form.engines || []).includes(engine);
							return (
								<button
									key={engine}
									type="button"
									className={`monitoring__engine-chip monitoring__engine-chip--toggle${
										selected ? " monitoring__engine-chip--selected" : ""
									}`}
									aria-pressed={selected}
									onClick={() => toggleEngine(engine)}
								>
									<ProviderIcon provider={engine} size={14} />
									{engineLabel(engine)}
								</button>
							);
						})}
					</div>
					<small className="monitoring__settings-hint text-muted">
						AI engines probed for this website (at least one).
					</small>
				</FormGroup>

				<FormGroup check className="monitoring__settings-check">
					<Input
						id="monitoring-web-search"
						type="checkbox"
						checked={form.enableWebSearch}
						onChange={(e) => patch({ enableWebSearch: e.target.checked })}
					/>
					<Label check for="monitoring-web-search">
						Enable web search during probing (grounded citation testing — a cost
						driver)
					</Label>
				</FormGroup>

				<FormGroup className="monitoring__settings-field monitoring__settings-field--timezone">
					<Label for="monitoring-timezone">Timezone</Label>
					<Input
						id="monitoring-timezone"
						type="select"
						value={form.timezone}
						onChange={(e) => patch({ timezone: e.target.value })}
					>
						{timezoneOptions.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</Input>
					<small className="monitoring__settings-hint text-muted">
						Applied across the dashboard — dates are stored in UTC and shown in
						this zone.
					</small>
				</FormGroup>
			</section>

			<section className="monitoring__panel monitoring__settings-panel">
				<h3 className="monitoring__panel-title">Alert thresholds</h3>

				<div className="monitoring__settings-grid">
					<FormGroup className="monitoring__settings-field">
						<Label for="monitoring-sov-drop">Share-of-voice drop (%)</Label>
						<Input
							id="monitoring-sov-drop"
							type="number"
							min={0}
							step={1}
							value={form.sovDropPct}
							onChange={(e) => patch({ sovDropPct: e.target.value })}
						/>
						<small className="monitoring__settings-hint text-muted">
							Alert when SoV / citation rate drops by more than this.
						</small>
					</FormGroup>

					<FormGroup className="monitoring__settings-field">
						<Label for="monitoring-score-drop">Score drop (points)</Label>
						<Input
							id="monitoring-score-drop"
							type="number"
							min={0}
							step={1}
							value={form.scoreDropPts}
							onChange={(e) => patch({ scoreDropPts: e.target.value })}
						/>
						<small className="monitoring__settings-hint text-muted">
							Alert when AEO or GEO score drops by more than this.
						</small>
					</FormGroup>
				</div>

				<FormGroup check className="monitoring__settings-check">
					<Input
						id="monitoring-citation-loss"
						type="checkbox"
						checked={form.citationLoss}
						onChange={(e) => patch({ citationLoss: e.target.checked })}
					/>
					<Label check for="monitoring-citation-loss">
						Alert on any citation lost on a tracked prompt
					</Label>
				</FormGroup>
			</section>

			<section className="monitoring__panel monitoring__settings-panel">
				<h3 className="monitoring__panel-title">Keyword research</h3>

				<div className="monitoring__settings-grid">
					<FormGroup className="monitoring__settings-field">
						<Label for="monitoring-keyword-region">Region</Label>
						<Input
							id="monitoring-keyword-region"
							type="text"
							maxLength={2}
							value={form.keywordRegion}
							onChange={(e) =>
								patch({ keywordRegion: e.target.value.toLowerCase() })
							}
							placeholder="us"
						/>
						<small className="monitoring__settings-hint text-muted">
							ISO country code for search volume (e.g. us, gb, in).
						</small>
					</FormGroup>

					<FormGroup className="monitoring__settings-field">
						<Label for="monitoring-keyword-min-volume">Min search volume</Label>
						<Input
							id="monitoring-keyword-min-volume"
							type="number"
							min={0}
							step={1}
							value={form.keywordMinVolume}
							onChange={(e) => patch({ keywordMinVolume: e.target.value })}
						/>
						<small className="monitoring__settings-hint text-muted">
							Ignore keywords below this monthly search volume.
						</small>
					</FormGroup>

					<FormGroup className="monitoring__settings-field">
						<Label for="monitoring-max-topics">Max topics</Label>
						<Input
							id="monitoring-max-topics"
							type="number"
							min={1}
							step={1}
							value={form.maxTopics}
							onChange={(e) => patch({ maxTopics: e.target.value })}
						/>
						<small className="monitoring__settings-hint text-muted">
							Cap on topic clusters returned by keyword research.
						</small>
					</FormGroup>

					<FormGroup className="monitoring__settings-field">
						<Label for="monitoring-prompts-primary">
							Primary prompts / keyword
						</Label>
						<Input
							id="monitoring-prompts-primary"
							type="number"
							min={1}
							max={3}
							step={1}
							value={form.promptsPrimaryPerKeyword}
							onChange={(e) =>
								patch({ promptsPrimaryPerKeyword: e.target.value })
							}
						/>
						<small className="monitoring__settings-hint text-muted">
							How many primary prompts to generate per keyword (1–3).
						</small>
					</FormGroup>

					<FormGroup className="monitoring__settings-field">
						<Label for="monitoring-prompts-subq">
							Sub-questions / keyword
						</Label>
						<Input
							id="monitoring-prompts-subq"
							type="number"
							min={0}
							max={10}
							step={1}
							value={form.promptsSubquestionsPerKeyword}
							onChange={(e) =>
								patch({ promptsSubquestionsPerKeyword: e.target.value })
							}
						/>
						<small className="monitoring__settings-hint text-muted">
							Follow-up / sub-question prompts per keyword (0–10).
						</small>
					</FormGroup>
				</div>

				<FormGroup check className="monitoring__settings-check">
					<Input
						id="monitoring-generate-branded"
						type="checkbox"
						checked={form.generateBrandedPrompts}
						onChange={(e) =>
							patch({ generateBrandedPrompts: e.target.checked })
						}
					/>
					<Label check for="monitoring-generate-branded">
						Generate branded prompts (include brand name in some prompts)
					</Label>
				</FormGroup>

				<FormGroup check className="monitoring__settings-check">
					<Input
						id="monitoring-autocurate"
						type="checkbox"
						checked={form.keywordPromptAutocurate}
						onChange={(e) =>
							patch({ keywordPromptAutocurate: e.target.checked })
						}
					/>
					<Label check for="monitoring-autocurate">
						Auto-curate generated keyword prompts into the tracked set
					</Label>
				</FormGroup>

				<FormGroup className="monitoring__settings-field">
					<Label for="monitoring-sov-prompt-scope">Share of Voice scope</Label>
					<Input
						id="monitoring-sov-prompt-scope"
						type="select"
						value={form.sovPromptScope || "unbranded"}
						onChange={(e) => patch({ sovPromptScope: e.target.value })}
					>
						<option value="unbranded">Unbranded only (competitive)</option>
						<option value="all">All prompts</option>
					</Input>
					<small className="monitoring__settings-hint text-muted">
						Branded prompts trivially name your brand, so they inflate SoV.
						&apos;Unbranded only&apos; is the industry-standard competitive
						measure; &apos;All prompts&apos; includes branded queries.
					</small>
				</FormGroup>
			</section>

			<section className="monitoring__panel monitoring__settings-panel">
				<h3 className="monitoring__panel-title">Crawl scope</h3>
				<p className="monitoring__settings-section-lead text-muted mb-0">
					{crawlDescription}
				</p>

				{crawl?.blocked ? (
					<div className="monitoring__crawl-warning" role="alert">
						Crawl appears blocked for this site. Discovery may be incomplete.
					</div>
				) : null}

				{crawlError ? (
					<div className="monitoring__crawl-error" role="alert">
						<span>{crawlError}</span>
						<Button color="link" size="sm" type="button" onClick={loadCrawl}>
							Retry
						</Button>
					</div>
				) : null}

				<FormGroup className="monitoring__settings-field">
					<Label for="monitoring-crawl-page-selection">Page selection</Label>
					<small className="monitoring__settings-hint text-muted d-block mb-2 mt-0">
						Auto keeps the baseline page set so scores stay comparable
						run-over-run.
					</small>
					<Input
						id="monitoring-crawl-page-selection"
						type="select"
						value={crawlMode}
						disabled={crawlLoading && !crawl}
						onChange={(e) => handleCrawlModeChange(e.target.value)}
					>
						{PAGE_SELECTION_OPTIONS.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</Input>
				</FormGroup>

				{crawlMode === "manual" ? (
					<div className="monitoring__crawl-manual-row">
						{crawlLoading ? (
							<p className="monitoring__crawl-inline-status text-muted mb-0">
								Loading pages… {pageCountLabel}
							</p>
						) : (
							<>
								<Button
									color="secondary"
									outline
									size="sm"
									type="button"
									onClick={() => setSelectOpen(true)}
									disabled={!crawl?.urls?.length}
								>
									Select pages
								</Button>
								<span className="monitoring__crawl-inline-status text-muted">
									{pageCountLabel}
								</span>
							</>
						)}
					</div>
				) : null}

				{crawlMode === "all" ? (
					crawlLoading ? (
						<div className="monitoring__crawl-info monitoring__crawl-info--warn">
							Counting the site&apos;s pages…
						</div>
					) : crawl ? (
						<div className="monitoring__crawl-info">
							Audits all {cappedCount} pages of {crawl.total} — capped at the
							configured ceiling of {crawl.ceiling}. Large page sets make each
							audit slower and more expensive.
						</div>
					) : null
				) : null}
			</section>

			<div className="monitoring__settings-actions">
				<Button
					color="primary"
					type="submit"
					className="monitoring__settings-save"
					disabled={saving || !isDirty}
				>
					{saving ? "Saving…" : "Save settings"}
				</Button>
			</div>

			<Modal
				isOpen={selectOpen}
				toggle={closeSelectModal}
				centered
				className="monitoring__crawl-pages-modal"
				backdropClassName="monitoring__crawl-pages-backdrop"
				contentClassName="monitoring__crawl-pages-content"
			>
				<div className="monitoring__crawl-pages-header">
					<div className="monitoring__crawl-pages-header-text">
						<h4 className="monitoring__crawl-pages-title">
							Select pages to crawl
						</h4>
						<p className="monitoring__crawl-pages-subtitle">
							Choose which pages the AI Grader will audit
						</p>
					</div>
					<button
						type="button"
						className="monitoring__crawl-pages-close"
						aria-label="Close"
						onClick={closeSelectModal}
					>
						<X size={16} strokeWidth={2} />
					</button>
				</div>

				<div className="monitoring__crawl-pages-search-wrap">
					<div className="monitoring__crawl-pages-search">
						<Search
							className="monitoring__crawl-pages-search-icon"
							size={16}
							strokeWidth={2}
							aria-hidden
						/>
						<input
							type="search"
							className="monitoring__crawl-pages-search-input"
							placeholder="Filter pages by URL..."
							value={filterInput}
							onChange={(e) => setFilterInput(e.target.value)}
							aria-label="Filter pages by URL"
						/>
						{filterInput ? (
							<button
								type="button"
								className="monitoring__crawl-pages-search-clear"
								aria-label="Clear filter"
								onClick={() => {
									setFilterInput("");
									setFilter("");
								}}
							>
								<X size={14} strokeWidth={2} />
							</button>
						) : null}
					</div>
				</div>

				<div className="monitoring__crawl-pages-toolbar">
					<label className="monitoring__crawl-pages-select-all">
						<input
							ref={selectAllRef}
							type="checkbox"
							checked={allFilteredSelected}
							onChange={handleSelectAllChange}
							disabled={filteredUrls.length === 0}
						/>
						<span>
							Select all ({filteredUrls.length.toLocaleString()})
						</span>
					</label>
					<div className="monitoring__crawl-pages-toolbar-right">
						{selectedCount > 0 ? (
							<button
								type="button"
								className="monitoring__crawl-pages-clear-btn"
								onClick={clearDraftSelection}
							>
								Clear selection
							</button>
						) : null}
						<select
							className="monitoring__crawl-pages-sort"
							value={sortBy}
							onChange={(e) => setSortBy(e.target.value)}
							aria-label="Sort pages"
						>
							{SORT_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</select>
					</div>
				</div>

				<p className="monitoring__crawl-pages-status">
					Showing {filteredUrls.length.toLocaleString()} of{" "}
					{listTotal.toLocaleString()} pages · {selectedCount.toLocaleString()}{" "}
					selected
				</p>

				<div className="monitoring__crawl-pages-list" role="list">
					{filteredUrls.length === 0 ? (
						<div className="monitoring__crawl-pages-empty">
							<Search size={48} strokeWidth={1.25} aria-hidden />
							<p className="monitoring__crawl-pages-empty-title">
								{filter.trim()
									? `No pages match '${filter.trim()}'`
									: "No pages available"}
							</p>
							{filter.trim() ? (
								<p className="monitoring__crawl-pages-empty-sub">
									Try a different search term
								</p>
							) : null}
						</div>
					) : (
						filteredUrls.map((url) => {
							const checked = draft.has(url);
							const id = `crawl-page-${encodeURIComponent(url)}`;
							return (
								<label
									key={url}
									htmlFor={id}
									className={`monitoring__crawl-pages-row${
										checked ? " monitoring__crawl-pages-row--selected" : ""
									}`}
									role="listitem"
									title={url}
								>
									<input
										id={id}
										type="checkbox"
										checked={checked}
										onChange={() => toggleDraftUrl(url)}
									/>
									<Globe
										className="monitoring__crawl-pages-row-icon"
										size={16}
										strokeWidth={2}
										aria-hidden
									/>
									<span className="monitoring__crawl-pages-row-url">
										{highlightUrlMatch(url, filter)}
									</span>
								</label>
							);
						})
					)}
				</div>

				<div className="monitoring__crawl-pages-footer">
					<Button color="secondary" outline type="button" onClick={closeSelectModal}>
						Cancel
					</Button>
					<Button
						color="primary"
						type="button"
						disabled={selectedCount < 1}
						onClick={handleConfirmPages}
					>
						Use {selectedCount} page{selectedCount === 1 ? "" : "s"}
						{selectedCount > 0 ? " →" : ""}
					</Button>
				</div>
			</Modal>

			<Modal
				isOpen={reauditOpen}
				toggle={handleReauditLater}
				centered
				className="monitoring__reaudit-scope-modal"
				backdrop
				keyboard={!reauditBusy}
			>
				<ModalHeader toggle={reauditBusy ? undefined : handleReauditLater}>
					Re-audit with the new crawl scope?
				</ModalHeader>
				<ModalBody>
					<p className="monitoring__reaudit-scope-body mb-0">
						You changed which pages are audited, so the current AEO/GEO scores
						were measured on a different page set and aren&apos;t comparable.
						Running a re-audit now realigns them. This uses your monthly budget
						and takes several minutes.
					</p>
				</ModalBody>
				<ModalFooter>
					<Button
						color="secondary"
						outline
						type="button"
						disabled={reauditBusy}
						onClick={handleReauditLater}
					>
						Later
					</Button>
					<Button
						color="primary"
						type="button"
						disabled={reauditBusy}
						onClick={handleReauditNow}
					>
						{reauditBusy ? "Starting…" : "Re-audit now"}
					</Button>
				</ModalFooter>
			</Modal>
		</form>
	);
}
